import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./HangmanGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import HangmanDrawing from "./HangmanDrawing";
import Keyboard from "./Keyboard";
import BalloonCelebration from "./BalloonCelebration";
import Confetti from "./Confetti";
import CelebrationSound from "./CelebrationSound";
import { useFirebase } from "../contexts/FirebaseContext";
import { isDemoMode, publishDemoState } from "../utils/demoMode";

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const HangmanGame = ({ onPointsChange, autoStart = false }) => {
  const { t, language } = useLanguage();
  const { recordGameEvent } = useFirebase();
  const [gameState, setGameState] = useState({
    gameId: null,
    displayWord: "",
    picture: "",
    category: "",
    wrongGuesses: 0,
    maxWrongGuesses: 6,
    gameOver: false,
    won: false,
    word: "",
  });

  const [guessedLetters, setGuessedLetters] = useState(new Set());
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const didAutoStartRef = useRef(false);

  useEffect(() => {
    publishDemoState({
      game: "hangman",
      ready: Boolean(gameState.gameId),
      gameOver: gameState.gameOver,
      won: gameState.won,
      displayWord: gameState.displayWord,
      picture: gameState.picture,
      word: gameState.word || null,
    });
  }, [gameState]);

  // Start a new game
  const startNewGame = useCallback(async () => {
    // Only show loading for longer operations
    const loadingTimeout = setTimeout(() => setLoading(true), 100);
    setMessage("");
    setGuessedLetters(new Set());

    try {
      const response = await fetch(`${API_BASE_URL}/api/game/new`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ language, demo: isDemoMode() }),
      });

      if (!response.ok) {
        throw new Error("Failed to start new game");
      }

      const data = await response.json();
      setGameState(data);
    } catch (error) {
      setMessage("Error starting new game. Please try again.");
      console.error("Error starting new game:", error);
    } finally {
      clearTimeout(loadingTimeout);
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    startNewGame();
  }, [autoStart, startNewGame]);

  // Make a guess
  const makeGuess = useCallback(
    async (letter) => {
      const upperLetter = letter.toUpperCase();
      
      if (
        !gameState.gameId ||
        gameState.gameOver ||
        guessedLetters.has(upperLetter)
      ) {
        return;
      }

      // Only show loading for longer operations
      const loadingTimeout = setTimeout(() => setLoading(true), 100);
      setMessage("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/game/${gameState.gameId}/guess`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ letter: upperLetter }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to make guess");
        }

        const data = await response.json();

        // Update guessed letters
        setGuessedLetters((prev) => new Set([...prev, upperLetter]));

        // Update game state
        setGameState(data);

        // Show message if provided
        if (data.message) {
          setMessage(data.message);
        }
      } catch (error) {
        setMessage(error.message || "Error making guess. Please try again.");
        console.error("Error making guess:", error);
      } finally {
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    },
    [gameState.gameId, gameState.gameOver, guessedLetters]
  );

  // Handle keyboard input
  const handleKeyPress = useCallback(
    (event) => {
      const key = event.key.toUpperCase();
      // Allow A-Z and German characters Ä, Ö, Ü
      if (/^[A-ZÄÖÜ]$/.test(key) && !guessedLetters.has(key)) {
        makeGuess(key);
      }
    },
    [guessedLetters, makeGuess]
  );

  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  // Start a new game on component mount
  useEffect(() => {
    startNewGame();
  }, [startNewGame]);

  // Notify parent about points changes and mission progress
  useEffect(() => {
    if (gameState.gameOver) {
      if (gameState.won) {
        onPointsChange(5);
        recordGameEvent("hangman_word_won");
      } else {
        onPointsChange(0);
        recordGameEvent("hangman_word_lost");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.gameOver, gameState.won, gameState.word]);

  // Memoize game status message to prevent unnecessary re-renders
  const gameStatusMessage = useMemo(() => {
    if (gameState.gameOver) {
      return gameState.won
        ? t("amazing")
        : t("greatTry");
    }
    return "";
  }, [gameState.gameOver, gameState.won, t]);

  // Memoize the message display to prevent layout shifts
  const messageDisplay = useMemo(() => {
    if (loading) {
      return <div className="message info">{t("loading")}</div>;
    }
    if (message) {
      return (
        <div
          className={`message ${message.includes("Error") || message.includes("Fehler") ? "error" : "info"}`}
        >
          {message}
        </div>
      );
    }
    return null;
  }, [loading, message, t]);

  // Memoize the game status display
  const gameStatusDisplay = useMemo(() => {
    if (gameStatusMessage) {
      return (
        <div className={`game-status ${gameState.won ? "won" : "lost"}`}>
          {gameStatusMessage}
        </div>
      );
    }
    return null;
  }, [gameStatusMessage, gameState.won]);

  return (
    <div className="hangman-game-container">
      <BalloonCelebration isVisible={gameState.gameOver && gameState.won} />
      <Confetti isVisible={gameState.gameOver && gameState.won} />
      <CelebrationSound isVisible={gameState.gameOver && gameState.won} />
      <div className="game-card" style={{ position: "relative" }}>
        <h1 className="game-title">{t("spellTheWord")}</h1>

        {messageDisplay}

        <div className="game-info">
          <span>
            {t("wrongGuesses")}: {gameState.wrongGuesses}/{gameState.maxWrongGuesses}
          </span>
          {gameState.category && (
            <span className="category">{t("category")}: {gameState.category}</span>
          )}
        </div>

        <HangmanDrawing wrongGuesses={gameState.wrongGuesses} gameOver={gameState.gameOver} won={gameState.won} />

        <div className="word-section">
          {gameState.picture && (
            <div className="picture-display">
              <span className="picture-emoji">{gameState.picture}</span>
            </div>
          )}
          <div className="word-display">
            {gameState.displayWord || t("loading")}
          </div>
        </div>

        {gameState.gameOver && gameState.word && (
          <div className="word-display">{t("theWordWas")} {gameState.word}</div>
        )}

        {gameStatusDisplay}

        <Keyboard
          guessedLetters={guessedLetters}
          onLetterClick={makeGuess}
          disabled={!gameState.gameId || gameState.gameOver || loading}
        />

        {(!gameState.gameId || gameState.gameOver) && (
          <button className="new-game-button" onClick={startNewGame} disabled={loading}>
            {loading ? t("loading") : !gameState.gameId ? t("startGame") : gameState.won ? t("playAgain") : t("tryAnotherWord")}
          </button>
        )}
      </div>
    </div>
  );
};

export default HangmanGame;

